import React from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; // eslint-disable-line
import { motion } from "framer-motion";
import {
  Target,
  Heart,
  ShieldCheck,
  Users,
  GraduationCap,
  Clock,
  Globe,
  ArrowRight,
} from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import CtaFinal from "components/cta/CtaFinal";
import { AppButton } from "components/misc/Buttons";
import useInView from "helpers/useInView";
import {
  fadeUpVariants,
  staggerContainerVariants,
  staggerItemVariants,
} from "lib/animations";
import { IMAGES } from "constants/images";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const VALEURS = [
  {
    icon: <Target size={28} />,
    title: "Excellence opérationnelle",
    desc: "Chaque dossier est traité avec rigueur. Nos délais sont garantis et nos documents conformes aux exigences des établissements partenaires.",
  },
  {
    icon: <Heart size={28} />,
    title: "Accompagnement humain",
    desc: "Derrière chaque étudiant, une équipe dédiée qui connaît les défis de la mobilité internationale et répond sous 2h en semaine.",
  },
  {
    icon: <ShieldCheck size={28} />,
    title: "Fiabilité & Transparence",
    desc: "Tarifs fixes, processus clair, aucun frais caché. Chaque AVI est vérifiable en ligne grâce à notre système de code QR sécurisé.",
  },
];

const STATS = [
  { value: "250+", label: "Étudiants accompagnés" },
  { value: "94%", label: "Taux de visa obtenu" },
  { value: "48h", label: "Délai moyen AVI" },
  { value: "4", label: "Pays de destination" },
];

const EQUIPE = [
  {
    name: "Mireille N.",
    role: "Fondatrice & Directrice",
    desc: "Ancienne expatriée, Mireille a créé MomsEduc Study après avoir vécu les difficultés administratives de la mobilité étudiante.",
  },
  {
    name: "Jean-Paul K.",
    role: "Responsable Dossiers AVI",
    desc: "Expert en attestations de virement irrévocable, Jean-Paul garantit la conformité de chaque document émis.",
  },
  {
    name: "Sandrine M.",
    role: "Conseillère Visa & Logement",
    desc: "10 ans d'expérience dans l'accompagnement des étudiants africains en mobilité vers la France et la Belgique.",
  },
];

// ─── Styled ──────────────────────────────────────────────────────────────────

const Section = styled.section<{ $bg?: string }>`
  background: ${(p) => p.$bg ?? "white"};
`;

const Wrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 5rem 1.5rem;
`;

// ── Mission ──────────────────────────────────────────────────────────────────

const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: 5fr 7fr;
  }
`;

const MissionImage = styled.div<{ $src: string }>`
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  min-height: 26rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom right,
      rgba(255, 108, 0, 0.15),
      transparent
    );
  }
`;

const MissionAccent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem 2rem;
  background: ${C_PRIMARY};
`;

const MissionAccentText = styled.p`
  color: white;
  font-weight: 900;
  font-size: 1rem;
  line-height: 1.4;
`;

const MissionContent = styled.div``;

const Eyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${C_PRIMARY};
  margin-bottom: 0.75rem;
`;

const Heading = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  line-height: 1.15;
  margin-bottom: 1.5rem;
`;

const Body = styled.p`
  color: #6b7280;
  line-height: 1.85;
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
`;

// ── Stats bar ────────────────────────────────────────────────────────────────

const StatsBar = styled.div`
  background: ${C_SECONDARY};
  padding: 3rem 1.5rem;
`;

const StatsGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCell = styled.div`
  padding: 1.5rem 2rem;
  border-right: 1px solid #1f1f1f;
  text-align: center;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 767px) {
    border-bottom: 1px solid #1f1f1f;

    &:nth-child(2n) {
      border-right: none;
    }
  }
`;

const StatVal = styled.p`
  font-size: 2.25rem;
  font-weight: 900;
  color: ${C_PRIMARY};
  line-height: 1;
  margin-bottom: 0.35rem;
`;

const StatLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.4);
`;

// ── Valeurs ──────────────────────────────────────────────────────────────────

const ValeursGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  border: 1px solid #e5e7eb;
  margin-top: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ValeurCard = styled.div`
  padding: 2.5rem 2rem;
  border-right: 1px solid #e5e7eb;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 767px) {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const ValeurIconWrap = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff4ec;
  color: ${C_PRIMARY};
  border-left: 3px solid ${C_PRIMARY};
  margin-bottom: 1.25rem;
`;

const ValeurTitle = styled.h3`
  font-size: 1rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.75rem;
`;

const ValeurDesc = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.75;
`;

// ── Équipe ───────────────────────────────────────────────────────────────────

const EquipeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  border: 1px solid #e5e7eb;
  margin-top: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const EquipeCard = styled.div`
  padding: 2rem;
  border-right: 1px solid #e5e7eb;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 767px) {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const EquipeAvatar = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  background: ${C_SECONDARY};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  color: ${C_PRIMARY};
`;

const EquipeName = styled.h3`
  font-size: 1rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.2rem;
`;

const EquipeRole = styled.p`
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C_PRIMARY};
  margin-bottom: 0.75rem;
`;

const EquipeDesc = styled.p`
  font-size: 0.82rem;
  color: #6b7280;
  line-height: 1.7;
`;

// ── Engagements ──────────────────────────────────────────────────────────────

const EngagementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 2.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const EngagementItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid #f0f0f0;
  background: white;
`;

const EngagementIcon = styled.div`
  flex-shrink: 0;
  color: ${C_PRIMARY};
  margin-top: 2px;
`;

const EngagementTitle = styled.p`
  font-size: 0.85rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.3rem;
`;

const EngagementDesc = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.65;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const AboutPage: React.FC = () => (
  <LandingLayout disableAnimation>
    {/* ── Hero ── */}
    <Hero
      backgroundImageSrc={IMAGES.studentsImg}
      notification="À propos"
      heading={
        <>
          <span>Derrière chaque étudiant,</span>
          <br />
          <SlantedBackground>une équipe engagée.</SlantedBackground>
        </>
      }
      description="MomsEduc Study est né d'un constat simple : les démarches administratives de la mobilité internationale sont un frein pour des milliers d'étudiants africains talentueux."
    />

    {/* ── Mission ── */}
    <Section>
      <Wrapper>
        <MissionGrid>
          <FadeUpSection>
            <MissionImage $src={IMAGES.studentsImg}>
              <MissionAccent>
                <MissionAccentText>
                  "Simplifier la mobilité étudiante, <br />
                  un dossier à la fois."
                </MissionAccentText>
              </MissionAccent>
            </MissionImage>
          </FadeUpSection>

          <FadeUpSection delay={0.15}>
            <MissionContent>
              <Eyebrow>Notre mission</Eyebrow>
              <Heading>
                Rendre la mobilité internationale accessible à tous.
              </Heading>
              <Body>
                Depuis notre création, nous accompagnons les étudiants d'Afrique
                subsaharienne dans toutes leurs démarches administratives pour
                étudier en Europe — de l'Attestation de Virement Irrévocable
                (AVI) jusqu'au logement et à la préparation du dossier visa.
              </Body>
              <Body>
                Nous savons que chaque semaine de retard peut compromettre une
                inscription universitaire. C'est pourquoi nous avons construit
                un processus fluide, des délais garantis et un accompagnement
                humain disponible 7j/7.
              </Body>
              <AppButton
                variant="primary"
                size="lg"
                to="/contact"
                iconRight={<ArrowRight size={16} />}
                style={{ marginTop: "1.5rem" }}
              >
                Nous contacter
              </AppButton>
            </MissionContent>
          </FadeUpSection>
        </MissionGrid>
      </Wrapper>
    </Section>

    {/* ── Stats ── */}
    <FadeUpSection>
      <StatsBar>
        <StatsGrid>
          {STATS.map((s) => (
            <StatCell key={s.label}>
              <StatVal>{s.value}</StatVal>
              <StatLabel>{s.label}</StatLabel>
            </StatCell>
          ))}
        </StatsGrid>
      </StatsBar>
    </FadeUpSection>

    {/* ── Valeurs ── */}
    <Section>
      <Wrapper>
        <FadeUpSection>
          <Eyebrow>Nos valeurs</Eyebrow>
          <Heading>Ce qui nous guide au quotidien.</Heading>
        </FadeUpSection>

        <MDiv
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          <ValeursGrid>
            {VALEURS.map((v) => (
              <MDiv key={v.title} variants={staggerItemVariants}>
                <ValeurCard>
                  <ValeurIconWrap>{v.icon}</ValeurIconWrap>
                  <ValeurTitle>{v.title}</ValeurTitle>
                  <ValeurDesc>{v.desc}</ValeurDesc>
                </ValeurCard>
              </MDiv>
            ))}
          </ValeursGrid>
        </MDiv>
      </Wrapper>
    </Section>

    {/* ── Équipe ── */}
    <Section $bg="#fafafa">
      <Wrapper>
        <FadeUpSection>
          <Eyebrow>Notre équipe</Eyebrow>
          <Heading>Des experts à votre service.</Heading>
        </FadeUpSection>

        <MDiv
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          <EquipeGrid>
            {EQUIPE.map((m) => (
              <MDiv key={m.name} variants={staggerItemVariants}>
                <EquipeCard>
                  <EquipeAvatar>
                    <Users size={22} />
                  </EquipeAvatar>
                  <EquipeName>{m.name}</EquipeName>
                  <EquipeRole>{m.role}</EquipeRole>
                  <EquipeDesc>{m.desc}</EquipeDesc>
                </EquipeCard>
              </MDiv>
            ))}
          </EquipeGrid>
        </MDiv>
      </Wrapper>
    </Section>

    {/* ── Engagements ── */}
    <Section>
      <Wrapper>
        <FadeUpSection>
          <Eyebrow>Nos engagements</Eyebrow>
          <Heading>Pourquoi nous faire confiance ?</Heading>
        </FadeUpSection>

        <MDiv
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          <EngagementsGrid>
            {[
              {
                icon: <Clock size={20} />,
                title: "Délais garantis",
                desc: "Votre AVI est livrée en 48h ouvrées ou remboursée. Nos processus sont calibrés pour respecter les deadlines universitaires.",
              },
              {
                icon: <Globe size={20} />,
                title: "Expertise multi-pays",
                desc: "France, Belgique, Canada, Luxembourg — nous maîtrisons les spécificités administratives de chaque destination.",
              },
              {
                icon: <ShieldCheck size={20} />,
                title: "Documents vérifiables",
                desc: "Chaque AVI comporte un code QR unique permettant à tout consulat ou établissement de vérifier l'authenticité en temps réel.",
              },
              {
                icon: <GraduationCap size={20} />,
                title: "Partenariats établissements",
                desc: "Nos attestations sont reconnues par plus de 120 universités et écoles en Europe francophone.",
              },
            ].map((e) => (
              <MDiv key={e.title} variants={staggerItemVariants}>
                <EngagementItem>
                  <EngagementIcon>{e.icon}</EngagementIcon>
                  <div>
                    <EngagementTitle>{e.title}</EngagementTitle>
                    <EngagementDesc>{e.desc}</EngagementDesc>
                  </div>
                </EngagementItem>
              </MDiv>
            ))}
          </EngagementsGrid>
        </MDiv>
      </Wrapper>
    </Section>

    {/* ── CTA ── */}
    <CtaFinal
      label="Prêt à commencer ?"
      title="Lancez votre dossier aujourd'hui"
      description="Nos conseillers vous accompagnent de A à Z. Réponse sous 2h."
    />
  </LandingLayout>
);

export default AboutPage;
